import * as Yup from "yup";

/**
 * Generate a Yup validation schema from field config.
 * Supports:
 * - select, email, textarea, text
 * - phone (multi-country pattern)
 * - captcha (type: 'captcha' OR name === 'captchaToken')
 * - custom messages via field.message
 *
 * Field shape expectation (examples):
 * { name: 'email', type: 'email', required: true, label: 'Email Address' }
 * { name: 'requestType', type: 'select', required: true }
 * { name: 'message', type: 'textarea', required: true }
 * { name: 'phone', type: 'text', required: true }
 * { name: 'captchaToken', type: 'captcha', required: true }
 */
export const generateValidation = (fields = []) => {
  const shape = {};

  fields.forEach(field => {
    const {
      name,
      type,
      required = false,
      label = "",
      message, // custom message (preferred)
    } = field;

    // Base validator: trim + allow empty if not required (we'll add .required later when needed)
    let validator = Yup.string().trim().nullable();

    // Resolve a sensible default message if custom one is not provided
    const defaultRequiredMsg =
      message ||
      (type === "select"
        ? "message"
        : type === "email"
          ? "Please enter a valid email address"
          : type === "textarea"
            ? "Please enter your comment"
            : name === "captchaToken" || type === "captcha"
              ? "Please verify the captcha"
              : label
                ? `Please enter ${String(label).toLowerCase()}`
                : "This field is required");

    // Special-case validators
    if (type === "email") {
      const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
      const emailMsg = message || "Please enter a valid email address";
      validator = Yup.string().trim().matches(emailRegex, emailMsg);
      if (required) {
        validator = validator.required(emailMsg);
      } else {
        validator = validator.notRequired();
      }
      shape[name] = validator;
      return;
    }

    if (type === "textarea") {
      // Often want min length; keep just required message per your spec
      if (required) {
        validator = validator.required(message || "Please enter your comment");
      } else {
        validator = validator.notRequired();
      }
      shape[name] = validator;
      return;
    }

    if (type === "select") {
      // Typically a string with non-empty value
      if (required) {
        validator = validator.required(
          message || "Please select a request type"
        );
      } else {
        validator = validator.notRequired();
      }
      shape[name] = validator;
      return;
    }

    // Phone: by name (per your current code)
    if (name === "phone") {
      // Allow only digits and +; then specific country patterns
      let phoneValidator = Yup.string()
        .trim()
        .matches(/^[0-9+]+$/, "Only numbers are allowed")
        .matches(
          /^(\+84|84|0)?[3|5|7|8|9][0-9]{8}$|^(\+62|62|0)?8[0-9]{8,11}$|^(\+91|91)?[6-9][0-9]{9}$/,
          "Enter valid mobile number"
        );

      if (required) {
        phoneValidator = phoneValidator.required("Enter valid mobile number");
      } else {
        phoneValidator = phoneValidator.notRequired();
      }

      shape[name] = phoneValidator;
      return;
    }

    // CAPTCHA: support by type OR by well-known name
    if (type === "captcha" || name === "captchaToken") {
      if (required) {
        validator = validator.required(message || "Please verify the captcha");
      } else {
        validator = validator.notRequired();
      }
      shape[name] = validator;
      return;
    }

    // Generic text fields
    if (type === "text") {
      if (required) {
        validator = validator.required(defaultRequiredMsg);
      } else {
        validator = validator.notRequired();
      }
      shape[name] = validator;
      return;
    }

    // Fallback: unknown types
    // If marked required, enforce; else optional
    if (required) {
      validator = validator.required(defaultRequiredMsg);
    } else {
      validator = validator.notRequired();
    }
    shape[name] = validator;
  });

  return Yup.object().shape(shape);
};

export const successText = {
  en: `Thank you for your enquiry! <br /> We will get back to you soon.`,
  vn: `Cám ơn câu hỏi của bạn! <br /> Chúng tôi sẽ liên lạc với bạn sớm nhất có thể.`,
};
